import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { PLATFORM_DATA_EVENT_NAME, waitForPlatformDataEvent } from '@/app/common/tracking/platformDataUtils.ts';

/**
 * Page-wide bootstrap of BIFF, the internal ads client (`window.brg.ui.biff`, biff-ui 8.x).
 *
 * The script is injected as early as possible and independently of the cookie banner: biff itself holds
 * `load()` and `installBanners()` back until GTM signals consent through `window.LMN_COOKIES_GO` (flag or
 * window event of the same name), so we never wait for `cookieGO` here. Everything talks to biff through the
 * `brg.ui.biff.cmd` command queue, which works both before and after the script has arrived.
 *
 * Integration guide: content-hub-pink/packages/biff-ui/INTEGRATION.md
 */

export const BIFF_SCRIPT_ID = 'biff-loader';
export const BIFF_SCRIPT_VERSION = '8';
export const BIFF_API_VERSION = 'v4';
export const LMN_COOKIES_GO_EVENT = 'LMN_COOKIES_GO';

const SLOW_CONNECTION_TYPES = ['slow-2g', '2g'];
const MAX_EMPTY_SLOT_REFRESHES = 5;
const FIRST_EMPTY_SLOT_REFRESH_DELAY_MS = 2000;
const NEXT_EMPTY_SLOT_REFRESH_DELAY_MS = 10000;

export const getBiffDomain = (isDev: boolean) => (isDev ? 'https://qa-cdn.biff.travel' : 'https://cdn.biff.travel');

export const getBiffSrc = (isDev: boolean) => `${getBiffDomain(isDev)}/scripts/${BIFF_SCRIPT_VERSION}/biff.min.js`;

/** No trailing slash: biff builds `${endpoint}/${platform}/${brand}/content` itself. */
export const getBiffEndpoint = (isDev: boolean) => `${getBiffDomain(isDev)}/api/${BIFF_API_VERSION}`;

/** Same contract as `prepareBiffQueue` from @lmn/contenthub-utilities, inlined because that package is only a transitive dependency. */
export const prepareBiffQueue = (): BiffQueue => {
  if (!window.brg) window.brg = {};
  const { brg } = window;
  if (!brg.ui) brg.ui = {};
  const { ui } = brg;
  if (!ui.biff) ui.biff = {};
  const { biff } = ui;
  if (!biff.cmd) biff.cmd = [];
  return biff.cmd;
};

/** Runs `command` once biff is ready (immediately if it already is). */
export const biffCmd = (command: BiffCommand): void => {
  if (!canUseDOM) return;
  prepareBiffQueue().push(command);
};

export const hasBiffScript = (): boolean => canUseDOM && !!document.getElementById(BIFF_SCRIPT_ID);

/**
 * True on a slow connection (2g / slow-2g) or with data-saver enabled. Devices without the Network Information
 * API count as fast. Only the hotel detail page skips its ads on this, as before biff 8; the search page never did.
 */
export const hasSlowConnection = (): boolean => {
  if (!canUseDOM) return false;

  const connection = navigator?.connection;
  if (connection?.saveData) return true;
  return !!connection?.effectiveType && SLOW_CONNECTION_TYPES.includes(connection.effectiveType);
};

let emptySlotRefreshAttached = false;

/**
 * Retries slots that rendered empty: once after 2s, then every 10s, up to 5 times per slot. Needs gpt.js,
 * which biff loads while resolving `load()`, so call it from that promise.
 */
export const attachEmptySlotRefresh = (): void => {
  if (emptySlotRefreshAttached || !canUseDOM) return;

  const pubads = window.googletag?.pubads?.();
  if (!pubads) return;

  emptySlotRefreshAttached = true;
  const refreshes: Record<string, number> = {};

  pubads.addEventListener('slotRenderEnded', (event) => {
    if (!event.isEmpty) return;

    const slotId = event.slot.getSlotElementId();
    const attempt = refreshes[slotId] || 0;
    refreshes[slotId] = attempt + 1;
    if (attempt >= MAX_EMPTY_SLOT_REFRESHES) return;

    setTimeout(
      () => window.googletag?.pubads?.().refresh([event.slot]),
      attempt === 0 ? FIRST_EMPTY_SLOT_REFRESH_DELAY_MS : NEXT_EMPTY_SLOT_REFRESH_DELAY_MS
    );
  });
};

type BiffBootParams = {
  /** QA or local: QA CDN + QA API endpoint. */
  isDev: boolean;
  /** Local dev only: GTM is not loaded there, so nothing would ever grant consent to biff. */
  isLocal: boolean;
};

/** Injects the biff script once per page. Safe to call repeatedly. */
export const bootBiff = ({ isDev, isLocal }: BiffBootParams): void => {
  if (!canUseDOM || hasBiffScript()) return;

  // Both must be in place before the tag is appended: biff reads the flag at init time.
  if (isLocal) window.LMN_COOKIES_GO = true;
  window.addEventListener(
    LMN_COOKIES_GO_EVENT,
    () => {
      // Should GTM dispatch the event before biff has initialised, keep it as the flag biff reads at init.
      window.LMN_COOKIES_GO = true;
    },
    { once: true, passive: true }
  );

  // First command in the queue, so it precedes any load().
  if (isDev) biffCmd((biff) => biff.helpers.setEndpoint(getBiffEndpoint(true)));

  const script = document.createElement('script');
  script.id = BIFF_SCRIPT_ID;
  script.src = getBiffSrc(isDev);
  script.async = true;
  document.head.appendChild(script);
};

type LoadBiffParams = {
  isLoggedIn: boolean;
  /** Identifies the page configuration; `load()` runs again only when it changes (e.g. search → hotel detail). */
  key: string;
  /** `window.platformData.length` when the page mounted, so only this page's visit event is awaited. */
  fromIndex?: number;
  signal?: AbortSignal;
};

type KeyedLoad = {
  /** Resolves once `load()` for this key has completed; a failed or aborted attempt leaves it pending for a retry. */
  loaded: Promise<void>;
  resolveLoaded: () => void;
  /** The attempt in flight or completed; cleared again when it resolved `false`, so the next `loadBiff` retries. */
  request?: Promise<boolean>;
  /** Whether `request` has settled. */
  settled: boolean;
};

const createKeyedLoad = (): KeyedLoad => {
  let resolveLoaded: KeyedLoad['resolveLoaded'] = () => {};
  const loaded = new Promise<void>((resolve) => {
    resolveLoaded = resolve;
  });
  return { loaded, resolveLoaded, settled: false };
};

/** One entry per configuration requested or awaited since biff last loaded a different one. */
const loads = new Map<string, KeyedLoad>();

const getKeyedLoad = (key: string): KeyedLoad => {
  const existing = loads.get(key);
  if (existing) return existing;

  const keyedLoad = createKeyedLoad();
  loads.set(key, keyedLoad);
  return keyedLoad;
};

type QueuedLoad = {
  key: string;
  /** Settles the attempt as `false` without waiting for biff. */
  giveUp: () => void;
};

/** The `load()` most recently handed to biff and not settled yet. */
let queuedLoad: QueuedLoad | undefined;

/**
 * Resolves once biff has finished `load()` for the given page configuration, i.e. after consent, the
 * configuration fetch and gpt.js. Anything that reaches into gpt or registers slots for that configuration has
 * to wait for this: see `loadBiff` for why slot registration cannot be queued early. Keyed on purpose: a
 * waiter for the detail page must not be released by a slow results-page `load()` finishing after a
 * client-side navigation. Safe to call before `loadBiff` for the key (child effects run before parent effects).
 */
export const whenBiffLoaded = (key: string): Promise<void> => getKeyedLoad(key).loaded;

/** True while a `loadBiff` for this key is in flight or has completed since biff last loaded another configuration. */
export const isBiffLoadRequested = (key: string): boolean => !!loads.get(key)?.request;

const runLoad = ({ isLoggedIn, key, signal }: LoadBiffParams): Promise<boolean> =>
  new Promise((resolve) => {
    const attempt: QueuedLoad = { key, giveUp: () => finish(false) };

    function finish(loaded: boolean) {
      if (queuedLoad === attempt) queuedLoad = undefined;
      signal?.removeEventListener('abort', attempt.giveUp);
      resolve(loaded);
    }

    if (signal?.aborted) {
      finish(false);
      return;
    }

    // A load() for another configuration supersedes the one before it, whatever consent is: before consent
    // biff 8 keeps a single resolver and the earlier load() is orphaned, its promise never settling; after
    // consent biff runs both, and the earlier one completing last would put its configuration — and its
    // slots — back over the newer page's. Settle the earlier attempt here either way, so it installs nothing
    // and leaves its key free for a retry (a hotel detail page opened, left for the results, opened again).
    queuedLoad?.giveUp();
    queuedLoad = attempt;
    // The page left before biff answered (HotelDetailBiff unmount): nothing will use the answer.
    signal?.addEventListener('abort', attempt.giveUp, { once: true });

    biffCmd((biff) => {
      // Queued commands run when biff arrives and again are held until consent, so an attempt given up in
      // the meantime gets here long after it settled: skipping load() keeps it from replacing the slots of
      // whichever configuration the page has moved on to.
      if (signal?.aborted || queuedLoad !== attempt) return;

      biff
        .withAuctions()
        .load({ isLoggedIn })
        .then(() => {
          attachEmptySlotRefresh();
          finish(true);
        })
        .catch((error) => {
          console.error('[biff] load failed', { key, error });
          finish(false);
        });
    });
  });

const requestLoad = async (params: LoadBiffParams): Promise<boolean> => {
  const visitTracked = await waitForPlatformDataEvent({
    eventName: PLATFORM_DATA_EVENT_NAME.VISIT,
    fromIndex: params.fromIndex ?? 0,
    signal: params.signal,
  });

  // Best effort, not a precondition: a page that never pushes a visit event (analyticsResultsEvent stays
  // silent without a destination parameter) would otherwise get no ads at all, where before biff 8 it got
  // ads without page context. Load anyway and let biff fall back. An aborted wait is not this case —
  // runLoad sees the same signal and settles false without calling biff.
  if (!visitTracked && !params.signal?.aborted) {
    console.warn('[biff] no visit event in platformData, loading without page context', { key: params.key });
  }

  return runLoad(params);
};

/**
 * Asks biff for the page configuration once the page's `visit` event is in `platformData` (biff builds the
 * request from `page.*` and `transactionAffiliation` there), or once waiting for it has timed out. Biff
 * queues the call until consent.
 *
 * Resolves `true` once that configuration's slots are defined, `false` when the page was left (`signal`), the
 * load was superseded by another configuration, or it failed. Register the page's slots only after it resolves
 * `true`: biff runs `installBanners` at once while the previous configuration's slots are still the defined
 * ones, and drops every id it does not know. Repeated calls for the same key share one promise; a `false`
 * outcome lets a later call try again, and so does a completed `load()` for another key, since that replaces
 * this key's slots (e.g. RESULTS after a client-side results → hotel detail → new search round trip).
 */
export const loadBiff = (params: LoadBiffParams): Promise<boolean> => {
  if (!canUseDOM) return Promise.resolve(false);

  const keyedLoad = getKeyedLoad(params.key);
  if (keyedLoad.request) return keyedLoad.request;

  const request = requestLoad(params)
    .catch((error) => {
      console.error('[biff] load failed', { key: params.key, error });
      return false;
    })
    .then((loaded) => {
      keyedLoad.settled = true;
      if (loaded) {
        keyedLoad.resolveLoaded();
        // biff now holds this configuration and the others' slots are gone: forget the keys it has already
        // answered, so their next request runs load() again. An attempt still in flight cleans up after
        // itself when it settles, and a key only waited on (whenBiffLoaded before loadBiff) keeps its
        // waiters — dropping it would leave them on a promise nothing resolves any more.
        loads.forEach((other, otherKey) => {
          if (other !== keyedLoad && other.request && other.settled) loads.delete(otherKey);
        });
      } else if (keyedLoad.request === request) {
        keyedLoad.request = undefined;
        keyedLoad.settled = false;
      }
      return loaded;
    });
  keyedLoad.request = request;

  return request;
};

export const installBanners = (params: BiffInstallBannersParams): void => {
  biffCmd((biff) => biff.Includer.installBanners(params));
};

export const resetBiffLoaderForTests = (): void => {
  loads.clear();
  queuedLoad = undefined;
  emptySlotRefreshAttached = false;
};
